import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './api/firebase-config';
import { useState, useEffect } from 'react';

// Components
import SignIn from './components/auth/SignIn';
import SignUp from './components/auth/SignUp';
import ProductList from './components/products/ProductList';
import OrderList from './components/orders/OrderList';
import CreateProductType from './components/products/CreateProductType';
import AddStock from './components/products/AddStock';
import ProductDetails from './components/products/ProductDetails';
import GiftPoints from './components/user-admin/GiftPoints';
import UserManagement from './components/user-admin/UserManagement';
import TaskTools from './components/user-admin/TaskTools';
import WalkingGroupsAdmin from './components/walking-groups/WalkingGroupsAdmin';

// Auth protection wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);

    React.useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
        });

        return () => unsubscribe();
    }, []);

    if (isAuthenticated === null) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/admin/signin" />;
    }

    return <>{children}</>;
};

// Dropdown Navigation Component
const DropdownNav = ({ title, children, isActive }: { title: string; children: React.ReactNode; isActive: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isOpen && !(event.target as Element).closest('.dropdown-nav')) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div className="relative dropdown-nav">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive 
                        ? 'bg-blue-100 text-blue-700 border-blue-300' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
                {title}
                <svg 
                    className={`ml-1 h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className="absolute z-10 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 border border-gray-200">
                    <div className="py-1" role="menu">
                        {children}
                    </div>
                </div>
            )}
        </div>
    );
};

// Layout component
const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [userId, setUserId] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUserId(user?.uid || null);
        });
        return () => unsubscribe();
    }, []);

    const handleLogout = async () => {
        await auth.signOut();
        navigate('/admin/signin');
    };

    // Check if current route is in product section
    const isProductSection = location.pathname.startsWith('/admin/products') || location.pathname.startsWith('/admin/orders');
    
    // Check if current route is in user section
    const isUserSection = location.pathname.startsWith('/admin/gift-points') || location.pathname.startsWith('/admin/user-management');

    // Check if current route is in walking groups section
    const isWalkingGroupsSection = location.pathname.startsWith('/admin/walking-groups');

    // Check if current route is in qa section
    const isQaSection = location.pathname.startsWith('/admin/qa');

    return (
        <div className="min-h-screen bg-gray-100">
            <nav className="bg-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex justify-between h-16">
                        <div className="flex">
                            <div className="flex-shrink-0 flex items-center">
                                <h1 className="text-xl font-bold">Vi Andas Admin</h1>
                            </div>
                            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                                {/* Product Section Dropdown */}
                                <DropdownNav title="Products" isActive={isProductSection}>
                                    <Link
                                        to="/admin/products"
                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                        role="menuitem"
                                    >
                                        <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                        </svg>
                                        Product List
                                    </Link>
                                    <Link
                                        to="/admin/products/create"
                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                        role="menuitem"
                                    >
                                        <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Create Product
                                    </Link>
                                    <Link
                                        to="/admin/orders"
                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                        role="menuitem"
                                    >
                                        <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                        </svg>
                                        Orders
                                    </Link>
                                </DropdownNav>

                                {/* User Section Dropdown */}
                                <DropdownNav title="Users" isActive={isUserSection}>
                                    <Link
                                        to="/admin/user-management"
                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                        role="menuitem"
                                    >
                                        <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                        </svg>
                                        User Management
                                    </Link>
                                    <Link
                                        to="/admin/gift-points"
                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                        role="menuitem"
                                    >
                                        <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                        </svg>
                                        Gift Points
                                    </Link>
                                </DropdownNav>

                                {/* Walking Groups Section */}
                                <DropdownNav title="Walking Groups" isActive={isWalkingGroupsSection}>
                                    <Link
                                        to="/admin/walking-groups"
                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                        role="menuitem"
                                    >
                                        <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        Manage Groups
                                    </Link>
                                </DropdownNav>

                                {/* QA Section Dropdown */}
                                <DropdownNav title="QA" isActive={isQaSection}>
                                    <Link
                                        to="/admin/qa/task-tools"
                                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                                        role="menuitem"
                                    >
                                        <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                        </svg>
                                        Task Tools
                                    </Link>
                                </DropdownNav>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <button
                                onClick={handleLogout}
                                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 mr-2"
                            >
                                Logout
                            </button>
                            {/* Mobile menu button */}
                            <div className="sm:hidden">
                                <button
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="sm:hidden">
                        <div className="pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
                            <div className="px-4 py-2">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Products</h3>
                                <div className="space-y-1">
                                    <Link
                                        to="/admin/products"
                                        className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                        </svg>
                                        Product List
                                    </Link>
                                    <Link
                                        to="/admin/products/create"
                                        className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Create Product
                                    </Link>
                                    <Link
                                        to="/admin/orders"
                                        className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                        </svg>
                                        Orders
                                    </Link>
                                </div>
                            </div>
                            <div className="px-4 py-2">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Users</h3>
                                <div className="space-y-1">
                                    <Link
                                        to="/admin/user-management"
                                        className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                        </svg>
                                        User Management
                                    </Link>
                                    <Link
                                        to="/admin/gift-points"
                                        className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                        </svg>
                                        Gift Points
                                    </Link>
                                </div>
                            </div>
                            <div className="px-4 py-2">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Walking Groups</h3>
                                <div className="space-y-1">
                                    <Link
                                        to="/admin/walking-groups"
                                        className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        Manage Groups
                                    </Link>
                                </div>
                            </div>
                            <div className="px-4 py-2">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">QA</h3>
                                <div className="space-y-1">
                                    <Link
                                        to="/admin/qa/task-tools"
                                        className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                        </svg>
                                        Task Tools
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </nav>
            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                {children}
            </main>
            <footer className="bg-gray-100 border-t mt-auto">
                <div className="max-w-7xl mx-auto px-4 py-3">
                    <p className="text-sm text-gray-500">User ID: {userId || 'Not logged in'}</p>
                </div>
            </footer>
        </div>
    );
};

// App Routes
const App = () => {
    return (
        <Router>
            <Routes>
                <Route path="/admin/signin" element={<SignIn />} />
                <Route path="/admin/signup" element={<SignUp />} />
                <Route
                    path="/admin/products/create"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <CreateProductType />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/products"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <ProductList />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/orders"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <OrderList />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/products/:productId/add-stock"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <AddStock />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/products/:productId"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <ProductDetails />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/gift-points"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <GiftPoints />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/user-management"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <UserManagement />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/walking-groups"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <WalkingGroupsAdmin />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/qa/task-tools"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <TaskTools />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route path="/admin" element={<Navigate to="/admin/products" replace />} />
            </Routes>
        </Router>
    );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>
); 